<script>
import { mapGetters } from 'vuex';
import AgentTable from './components/overview/AgentTable.vue';
import MetricCard from './components/overview/MetricCard.vue';
import { OVERVIEW_METRICS } from './constants';
import ReportHeatmap from './components/Heatmap.vue';

import endOfDay from 'date-fns/endOfDay';
import getUnixTime from 'date-fns/getUnixTime';
import startOfDay from 'date-fns/startOfDay';
import subDays from 'date-fns/subDays';
import { emitter } from 'shared/helpers/mitt';
import Spinner from 'shared/components/Spinner.vue';
import KpiCardContainer from './components/overview/KpiCardContainer.vue';
import AgentKpiContainer from './components/overview/AgentKpiContainer.vue';
import GroupContainer from './components/overview/GroupContainer.vue';
import InboxContainer from './components/overview/InboxContainer.vue';
import LabelContainer from './components/overview/LabelContainer.vue';
import AgentCard from './components/overview/AgentCard.vue';
import IntersectionObserver from 'dashboard/components/IntersectionObserver.vue'; 

export default {
  name: 'LiveReports',
  components: {
    AgentTable,
    MetricCard,
    ReportHeatmap,
    Spinner,
    KpiCardContainer,
    AgentKpiContainer,
    GroupContainer,
    InboxContainer,
    LabelContainer,
    AgentCard,
    IntersectionObserver,
  },
  data() {
    return {
      // always start with 0, this is to manage the pagination in tanstack table
      // when we send the data, we do a +1 to this value
      pageIndex: 0,
      intersectionObserverOptions : {
        root: null,
        rootMargin: '0px', 
        threshold: 0.2,
      }
    };
  },
  computed: {
    ...mapGetters({
      agentStatus: 'agents/getAgentStatus',
      agents: 'agents/getAgents',
      agentsMeta: 'agents/getMeta',
      accountConversationMetric: 'getAccountConversationMetric',
      agentConversationMetric: 'getAgentConversationMetric',
      accountConversationHeatmap: 'getAccountConversationHeatmapData',
      uiFlags: 'getOverviewUIFlags',
      teamMetrics: 'getTeamMetrics',
      inboxMetrics: 'getInboxMetrics',
      labelMetrics: 'getLabelMetrics',
    }),
    hasMoreAgents() {
      if (this.uiFlags.isFetchingAgentConversationMetric) {
        return true;
      }
      if (this.agentConversationMetric.length < 9) {
        return false;
      }      
      return this.agents.length > this.agentConversationMetric.length;
    },
    agentStatusMetrics() {
      let metric = {};
      Object.keys(this.agentStatus).forEach(key => {
        const metricName = this.$t(
          `OVERVIEW_REPORTS.AGENT_STATUS.${OVERVIEW_METRICS[key]}`
        );
        metric[metricName] = this.agentStatus[key];
      });
      return metric;
    },
    conversationMetrics() {
      let metric = {};
      const keys = Object.keys(this.accountConversationMetric);
      // Bỏ item index 1 ("Không giám sát")
      const filteredKeys = keys.filter((key, index) => index !== 1);
      filteredKeys.forEach(key => {
        const metricName = this.$t(
          `OVERVIEW_REPORTS.ACCOUNT_CONVERSATIONS.${OVERVIEW_METRICS[key]}`
        );
        metric[metricName] = this.accountConversationMetric[key];
      });
      return metric;
    },
  },
  mounted() {
    // Fetch a larger page of agents so we can join metrics reliably
    this.$store.dispatch('agents/get', { page: 1, perPage: 100 });
    this.fetchAllData();

    emitter.on('fetch_overview_reports', () => {
      this.fetchAllData();
    });
  },
  methods: {
    async fetchAllData() {
      await this.$store.dispatch('labels/get');
      this.fetchAccountConversationMetric();
      this.fetchAgentConversationMetric();
      this.fetchHeatmapData();
      this.fetchTeamMetrics();
      this.fetchInboxMetrics();
      this.fetchLabelMetrics();
    },
    downloadHeatmapData() {
      let to = endOfDay(new Date());

      this.$store.dispatch('downloadAccountConversationHeatmap', {
        to: getUnixTime(to),
      });
    },
    fetchHeatmapData() {
      if (this.uiFlags.isFetchingAccountConversationsHeatmap) {
        return;
      }

      // the data for the last 6 days won't ever change,
      // so there's no need to fetch it again
      // but we can write some logic to check if the data is already there
      // if it is there, we can refetch data only for today all over again
      // and reconcile it with the rest of the data
      // this will reduce the load on the server doing number crunching
      let to = endOfDay(new Date());
      let from = startOfDay(subDays(to, 6));

      if (this.accountConversationHeatmap.length) {
        to = endOfDay(new Date());
        from = startOfDay(to);
      }

      this.$store.dispatch('fetchAccountConversationHeatmap', {
        metric: 'conversations_count',
        from: getUnixTime(from),
        to: getUnixTime(to),
        groupBy: 'hour',
        businessHours: false,
      });
    },
    fetchAccountConversationMetric() {
      this.$store.dispatch('fetchAccountConversationMetric', {
        type: 'account',
      });
    },
    fetchAgentConversationMetric(append = false) {
      this.$store.dispatch('fetchAgentConversationMetric', {
        type: 'agent',
        page: this.pageIndex + 1,
        append: append,
      });
    },
    onPageNumberChange(pageIndex, append = false) {
      this.pageIndex = pageIndex;
      this.fetchAgentConversationMetric(append);
    },
    onLoadMoreAgents(){
      if(this.hasMoreAgents){
        this.onPageNumberChange(this.pageIndex + 1, true);
      }
    },
    fetchTeamMetrics() {
      // Sử dụng cùng thời gian với heatmap data
      let to = endOfDay(new Date());
      let from = startOfDay(subDays(to, 6));
      
      this.$store.dispatch('fetchTeamMetrics', {
        from: getUnixTime(from),
        to: getUnixTime(to),
        businessHours: false,
      });
    },
    fetchInboxMetrics() {
      // Sử dụng cùng thời gian với heatmap data
      let to = endOfDay(new Date());
      let from = startOfDay(subDays(to, 6));
      
      this.$store.dispatch('fetchInboxMetrics', {
        from: getUnixTime(from),
        to: getUnixTime(to),
        businessHours: false,
      });
    },
    fetchLabelMetrics() {
      this.$store.dispatch('fetchLabelMetrics');
    },
  },
};

</script>

<template>
  <div class="flex w-full flex-col gap-6 mt-4 md:gap-5 xl:gap-6">
    <div class="w-full">
      <KpiCardContainer
        :metrics="conversationMetrics"
        :is-loading="uiFlags.isFetchingAccountConversationMetric"
        :loading-message="$t('OVERVIEW_REPORTS.ACCOUNT_CONVERSATIONS.LOADING_MESSAGE')"
      />
    </div>
    <div class="w-full">
      <AgentKpiContainer :metrics="agentStatusMetrics" :is-loading="uiFlags.isFetchingAgentConversationMetric" />
    </div>
    <div class="w-full heatmap-section">
      <div class="rounded-xl bg-n-solid-2 p-6 shadow-sm border border-n-weak">
        <div class="flex items-center justify-between w-full h-10 gap-2 mb-4">
          <span class="text-sm md:text-base xl:text-lg font-semibold text-n-slate-12 dark:text-n-gray-3">
            {{ $t('OVERVIEW_REPORTS.CONVERSATION_HEATMAP.HEADER') }}
          </span>
          <woot-button 
            size="small" 
            variant="smooth" 
            color-scheme="secondary"
            class="dark:!bg-n-vars-black-br/30 dark:text-n-gray-2 px-3"
            @click="downloadHeatmapData"
          >
            {{ $t('REPORT.DOWNLOAD') }}
          </woot-button>
        </div>
        <div class="heatmap-metric-card">
          <ReportHeatmap :heat-data="accountConversationHeatmap"
            :is-loading="uiFlags.isFetchingAccountConversationsHeatmap" />
        </div>
      </div>
    </div>
    <div class="w-full">
      <GroupContainer
        :cards="teamMetrics"
        :is-loading="uiFlags.isFetchingTeamMetrics"
        title="OVERVIEW_REPORTS.TEAMS.HEADER"
      />
    </div>
    <div class="w-full">
      <InboxContainer
        :cards="inboxMetrics"
        :is-loading="uiFlags.isFetchingInboxMetrics"
        title="OVERVIEW_REPORTS.INBOXES.HEADER"
      />
    </div>
    <div class="w-full">
      <LabelContainer
        :metrics="labelMetrics"
        :is-loading="uiFlags.isFetchingLabelMetrics"
        title="OVERVIEW_REPORTS.LABELS.HEADER"
      />
    </div>
    <div class="w-full">
      <div class="mb-4">
        <h3 class="text-sm md:text-base xl:text-lg font-semibold text-n-slate-12 dark:text-n-gray-3">
          {{ $t('OVERVIEW_REPORTS.AGENT_CONVERSATIONS.HEADER') }}
        </h3>
      </div>
      
      <div class="block xl:hidden w-full">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <AgentCard
            v-for="agent in agentConversationMetric"
            :key="agent.id"
            :agent="agent"
          />
        </div>
        
        <div v-if="uiFlags.isFetchingAgentConversationMetric" class="flex justify-center items-center py-6">
          <div class="flex items-center space-x-3">
            <Spinner size="small" />
          </div>
        </div>
        
        <IntersectionObserver
          v-if="!uiFlags.isFetchingAgentConversationMetric && hasMoreAgents"
          :options="intersectionObserverOptions"
          @observed="onLoadMoreAgents"
        />
        
        <div v-if="!hasMoreAgents && !uiFlags.isFetchingAgentConversationMetric" class="text-center py-4">
          <span class="text-sm text-gray-500 dark:text-gray-400">{{ $t('OVERVIEW_REPORTS.AGENT_CONVERSATIONS.ALL_AGENTS_LOADED') }}</span>
        </div>
      </div>
      
      <div class="hidden xl:block w-full">
        <AgentTable
          :agents="agents"
          :agent-metrics="agentConversationMetric"
          :page-index="pageIndex"
          :total="agentsMeta?.count || 0"
          :page-size="9"
          :is-loading="uiFlags.isFetchingAgentConversationMetric"
          @page-change="onPageNumberChange"
        />
      </div>
    </div>
  </div>
</template>
